import React, { useState } from 'react';
import { AlignJustify, X, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavigation = (sectionId: string) => {
    if (location.pathname === '/') {
      // Already on homepage, just scroll to section
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to homepage with hash
      navigate(`/#${sectionId}`);
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-white/90 backdrop-blur-md shadow-md py-4 sticky top-0 z-50" role="banner">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity cursor-pointer">
            <img
              src="/logo/ventura-power-washing-horizontal.png"
              alt="Ventura Power Washing"
              width="900"
              height="330"
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <div className="relative group">
              <button className="text-gray-700 hover:text-ventura-blue transition-colors duration-300" aria-label="View our services" aria-expanded="false" aria-haspopup="true">Services</button>
              <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <div className="py-1">
                  <Link to="/services/pressure-washing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-ventura-blue">Pressure Washing</Link>
                  <Link to="/services/soft-wash" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-ventura-blue">Soft Wash</Link>
                  <Link to="/services/window-washing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-ventura-blue">Window Washing</Link>
                  <Link to="/services/carpet-cleaning" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-ventura-blue">Carpet Cleaning</Link>
                  <Link to="/services/gutter-cleaning" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-ventura-blue">Gutter Cleaning</Link>
                  <Link to="/services/solar-panel-cleaning" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-ventura-blue">Solar Panel Cleaning</Link>
                  <Link to="/services/dumpster-pad-cleaning" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-ventura-blue">Dumpster Pad Cleaning</Link>
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <Link to="/services" className="block px-4 py-2 text-sm font-semibold text-ventura-blue hover:bg-gray-100">View All Services →</Link>
                  </div>
                </div>
              </div>
            </div>
            <Link to="/areas" className="text-gray-700 hover:text-ventura-blue transition-colors duration-300" aria-label="View the cities and neighborhoods we serve">Areas</Link>
            <Link to="/about" className="text-gray-700 hover:text-ventura-blue transition-colors duration-300" aria-label="About Ventura Power Washing and our founder">About</Link>
            <button onClick={() => handleNavigation('testimonials')} className="text-gray-700 hover:text-ventura-blue transition-colors duration-300" aria-label="Navigate to Reviews section">Reviews</button>
            <Link to="/faq" className="text-gray-700 hover:text-ventura-blue transition-colors duration-300" aria-label="Frequently asked questions about our services">FAQ</Link>
            <Link to="/blog" className="text-gray-700 hover:text-ventura-blue transition-colors duration-300" aria-label="Read our blog with cleaning tips and guides">Blog</Link>
            <Button asChild className="rounded-full bg-ventura-green hover:bg-ventura-green/90 text-white">
              <Link to="/free-estimate" aria-label="Get a free estimate for power washing services">Free Estimate</Link>
            </Button>
            <Button 
              className="button-primary flex items-center gap-2 rounded-full" 
              onClick={() => window.location.href = "tel:+18054647166"}
              aria-label="Call Ventura Power Washing at 805-464-7166"
            >
              <Phone size={16} />
              <span>Call Ventura</span>
            </Button>
          </div>

          {/* Mobile Navigation - Toggle Button */}
          <div className="lg:hidden">
            <Button variant="ghost" onClick={toggleMenu} aria-label="Toggle Menu">
              {isMenuOpen ? <X size={24} /> : <AlignJustify size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation - Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 bg-white py-4 animate-fade-in">
            <div className="flex flex-col space-y-4">
              <div className="px-4">
                <p className="text-sm font-semibold text-gray-600 mb-2">Services:</p>
                <div className="space-y-1 ml-2">
                  <Link to="/services/pressure-washing" onClick={toggleMenu} className="block text-sm text-gray-700 hover:text-ventura-blue transition-colors">Pressure Washing</Link>
                  <Link to="/services/soft-wash" onClick={toggleMenu} className="block text-sm text-gray-700 hover:text-ventura-blue transition-colors">Soft Wash</Link>
                  <Link to="/services/window-washing" onClick={toggleMenu} className="block text-sm text-gray-700 hover:text-ventura-blue transition-colors">Window Washing</Link>
                  <Link to="/services/carpet-cleaning" onClick={toggleMenu} className="block text-sm text-gray-700 hover:text-ventura-blue transition-colors">Carpet Cleaning</Link>
                  <Link to="/services/gutter-cleaning" onClick={toggleMenu} className="block text-sm text-gray-700 hover:text-ventura-blue transition-colors">Gutter Cleaning</Link>
                  <Link to="/services/solar-panel-cleaning" onClick={toggleMenu} className="block text-sm text-gray-700 hover:text-ventura-blue transition-colors">Solar Panel Cleaning</Link>
                  <Link to="/services/dumpster-pad-cleaning" onClick={toggleMenu} className="block text-sm text-gray-700 hover:text-ventura-blue transition-colors">Dumpster Pad Cleaning</Link>
                  <Link to="/services" onClick={toggleMenu} className="block text-sm font-semibold text-ventura-blue transition-colors">View All Services →</Link>
                </div>
              </div>
              <Link to="/areas" onClick={toggleMenu} className="text-gray-700 hover:text-ventura-blue px-4 py-2 transition-colors duration-300 text-left" aria-label="View the cities and neighborhoods we serve">Areas</Link>
              <Link to="/about" onClick={toggleMenu} className="text-gray-700 hover:text-ventura-blue px-4 py-2 transition-colors duration-300 text-left" aria-label="About Ventura Power Washing and our founder">About</Link>
              <button onClick={() => handleNavigation('testimonials')} className="text-gray-700 hover:text-ventura-blue px-4 py-2 transition-colors duration-300 text-left" aria-label="Navigate to Reviews section">Reviews</button>
              <Link to="/faq" onClick={toggleMenu} className="text-gray-700 hover:text-ventura-blue px-4 py-2 transition-colors duration-300 text-left" aria-label="Frequently asked questions about our services">FAQ</Link>
              <Link to="/blog" onClick={toggleMenu} className="text-gray-700 hover:text-ventura-blue px-4 py-2 transition-colors duration-300 text-left" aria-label="Read our blog with cleaning tips and guides">Blog</Link>
              <div className="px-4">
                <Button asChild className="w-full rounded-full bg-ventura-green hover:bg-ventura-green/90 text-white">
                  <Link to="/free-estimate" onClick={toggleMenu} aria-label="Get a free estimate for power washing services">Free Estimate</Link>
                </Button>
              </div>
              <div className="px-4">
                <Button className="w-full button-primary flex items-center justify-center gap-2" onClick={() => window.location.href = "tel:+18054647166"} aria-label="Call Ventura Power Washing at 805-464-7166">
                  <Phone size={16} />
                  <span>Call Ventura Now</span>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
